import { Outlet, useNavigate, useParams } from 'react-router-dom';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { SdpFullStatus, SdpResponse } from '../../models/SdpResponse';
import { UIStateProps, uiState } from '../../atoms/uiAtom';
import { sdpState, sdpStateGroupedSdps } from '../../atoms/sdpAtom';
import { useDeleteSdps, useGetOverview } from '../../hooks/sdps';
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';

import { ReactComponent as BurgerIcon } from '../../assets/burger.svg';
import Button from '../common/Button';
import { ReactComponent as ChevronLeftIcon } from '../../assets/chevron-left.svg';
import { ReactComponent as CloseIcon } from '../../assets/close.svg';
import Container from '../common/Container';
import { EanURL } from '../../common/eanUtils';
import EmptyState from '../common/EmptyState';
import { ReactComponent as ImportantIcon } from '../../assets/important.svg';
import Loader from '../common/Loader';
import ModalDeleteMeter from '../modals/ModalDeleteMeter';
import { ModalResponse } from '../../enum/ModalResponse';
import OverviewMeterItem from './OverviewMeterItem';
import OverviewMeters from './OverviewMeters';
import { SDPStatusEnum } from '../../enum/SDPStatusEnum';
import Skeleton from 'react-loading-skeleton';
import classNames from 'classnames';
import { meterOnboardingState } from '../../atoms/meterOnboardingWizardAtom';
import { overviewTabs } from './OverviewMeterDetails';
import { roleAccessState } from '../../atoms/roleAccessAtom';
import { routes } from '../../routes';
import { useGetRolesAccess } from '../../hooks/auth';
import useScreenType from 'react-screentype-hook';
import { useTranslation } from 'react-i18next';
import { userPreferencesState } from '../../atoms/userPreferencesAtom/userPreferencesAtom';

export default function Overview() {
  const { t } = useTranslation();
  const { id: sdpId, tabId: selectedTabId } = useParams();
  const screenType = useScreenType();
  const {
    isFetching: isFetchingGetOverview,
    data: sdpsData,
    isError: isGetOverviewError,
    refetch: refetchGetOverview,
  } = useGetOverview();
  const { isPending: isPendingDeleteMeters, mutateAsync: deleteMeters } = useDeleteSdps();

  const setUiState = useSetRecoilState<UIStateProps>(uiState);
  const [userPreferences, setUserPreferences] = useRecoilState(userPreferencesState);

  const [isOpenModalDeleteMeter, setIsOpenModalDeleteMeter] = useState(false);
  const [sdpAtom, setSdpState] = useRecoilState(sdpState);
  const groupedSdps = useRecoilValue(sdpStateGroupedSdps);
  const [checkedSdpIds, setCheckedSdpIds] = useState<string[]>([]);
  const [isOverviewListEditMode, setIsOverviewListEditMode] = useState(false);
  const [hasOverviewMetersList, setHasOverviewMetersList] = useState(true);
  const navigate = useNavigate();
  const resetAddMeterWizardState = useResetRecoilState(meterOnboardingState);
  const {
    data: dataGetRolesAccess,
    isError: isErrorGetRolesAccess,
    isFetching: isFetchingGetRolesAccess,
  } = useGetRolesAccess(groupedSdps.sdps.length > 0 ? userPreferences.selectedSdpId : undefined);
  const [roleAccess, setRoleAccess] = useRecoilState(roleAccessState);

  useEffect(() => {
    if (
      dataGetRolesAccess?.data &&
      isErrorGetRolesAccess === false &&
      isFetchingGetRolesAccess === false
    ) {
      setRoleAccess(dataGetRolesAccess.data);
    }
  }, [dataGetRolesAccess, isErrorGetRolesAccess, isFetchingGetRolesAccess, setRoleAccess]);

  useEffect(() => {
    if (
      sdpsData?.data &&
      sdpsData!.data!.length === 0 &&
      isFetchingGetOverview === false &&
      isGetOverviewError === false
    ) {
      setHasOverviewMetersList(false);
    }
  }, [sdpsData, sdpsData?.data, isFetchingGetOverview, isGetOverviewError]);

  const handleClickOverviewMeterItem = useCallback(
    ({ sdp }: { event?: React.MouseEvent<HTMLDivElement, MouseEvent>; sdp: SdpResponse }) => {
      if (roleAccess) {
        if (screenType.isMobile) {
          handleClickCloseOverviewMeterList();
        }
        const tab = overviewTabs(roleAccess).filter(
          (tab) => tab.isAvailable && tab.id === selectedTabId
        )[0];
        navigate(
          `${routes.withArgs(
            `${routes.OVERVIEW.path}${routes.OVERVIEW.children.OVERVIEW_DETAIL.path}`,
            {
              ':id': EanURL.encrypt(sdp.id) as string,
              ':tabId': tab
                ? tab.id
                : overviewTabs(roleAccess).filter((tab) => tab.isAvailable)[0].id,
            }
          )}${window.location.search}`
        );
      }
    },
    [navigate, roleAccess, screenType.isMobile, selectedTabId]
  );

  useEffect(() => {
    if (
      sdpsData?.data &&
      sdpsData!.data!.length > 0 &&
      isFetchingGetOverview === false &&
      isGetOverviewError === false
    ) {
      const selectedSdp =
        sdpsData!.data!.find((sdp) => sdp.id === EanURL.decrypt(sdpId!)) ??
        groupedSdps.ownSdps[0] ??
        groupedSdps.otherSdps[0];
      setUserPreferences((state) => ({
        ...state,
        selectedSdpId: selectedSdp?.id,
      }));
      if (selectedSdp) {
        handleClickOverviewMeterItem({
          sdp: selectedSdp,
        });
      }
    }
  }, [
    handleClickOverviewMeterItem,
    groupedSdps.ownSdps,
    isFetchingGetOverview,
    isGetOverviewError,
    sdpsData,
    setUserPreferences,
    userPreferences.selectedSdpId,
    sdpId,
    groupedSdps.otherSdps,
  ]);
  useEffect(() => {
    setUiState((prev) => ({ ...prev, hasError: isGetOverviewError }));
    if (
      isFetchingGetOverview === false &&
      isGetOverviewError === false &&
      sdpsData?.data &&
      sdpsData!.data!.length > 0
    ) {
      setSdpState((state) => ({
        ...state,
        items: sdpsData?.data,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setUiState, isGetOverviewError, isFetchingGetOverview, setSdpState, sdpsData?.data]);

  const handleCheckedOverviewMeterItem = ({
    sdp,
  }: {
    event: React.MouseEvent<HTMLDivElement, MouseEvent>;
    sdp: SdpResponse;
  }) => {
    if (checkedSdpIds.includes(sdp.id)) {
      setCheckedSdpIds((prevState) => prevState.filter((id) => id !== sdp.id));
    } else {
      setCheckedSdpIds((prevState) => [...prevState, sdp.id]);
    }
  };

  const handleClickEditOverviewMeterList = () => {
    setIsOverviewListEditMode((prevState) => !prevState);
  };

  const renderMetersGroup = ({
    title,
    sdps,
    isEditable = false,
  }: {
    title: string;
    sdps?: SdpResponse[];
    isEditable?: boolean;
  }) => {
    return (
      <OverviewMeters
        title={title}
        isDisabled={isPendingDeleteMeters}
        isLoading={isGetOverviewError || isFetchingGetOverview}
        onClickEdit={isEditable ? handleClickEditOverviewMeterList : undefined}
      >
        {sdps?.map((sdp) => (
          <OverviewMeterItem
            key={sdp.id}
            sdp={sdp}
            isDisabled={isPendingDeleteMeters}
            isChecked={checkedSdpIds.includes(sdp.id)}
            isActive={sdp.id === userPreferences.selectedSdpId}
            onClick={
              SdpFullStatus(sdp) !== SDPStatusEnum.WAITING &&
              SdpFullStatus(sdp) !== SDPStatusEnum.EXPIRED
                ? handleClickOverviewMeterItem
                : undefined
            }
            onChecked={
              isOverviewListEditMode && SdpFullStatus(sdp) !== SDPStatusEnum.WAITING
                ? handleCheckedOverviewMeterItem
                : undefined
            }
          />
        ))}
      </OverviewMeters>
    );
  };

  const handleClickDeleteOverviewMeterItemList = () => {
    setIsOpenModalDeleteMeter(true);
  };

  const handleClickCancelOverviewMeterList = () => {
    setIsOverviewListEditMode(false);
    setCheckedSdpIds([]);
  };

  const renderFixedBottomElement = () => {
    if (isGetOverviewError || isFetchingGetOverview) {
      return <Skeleton count={1} height={36} width="100%" />;
    }
    return isOverviewListEditMode ? (
      <Fragment>
        <Button
          isDisabled={isPendingDeleteMeters}
          className="btn btn--secondary"
          onClick={handleClickCancelOverviewMeterList}
        >
          {t('common.cancel')}
        </Button>
        <Button
          prefix={isPendingDeleteMeters ? <Loader /> : null}
          isDisabled={checkedSdpIds.length === 0 || isPendingDeleteMeters}
          className="btn btn--primary"
          onClick={handleClickDeleteOverviewMeterItemList}
        >
          {t('common.delete')}
        </Button>
      </Fragment>
    ) : (
      <Fragment>
        <Button onClick={handleClickAddEan} className="btn btn--primary">
          {t('overview.add.meter')}
        </Button>
      </Fragment>
    );
  };

  const handleClickModalDeleteMeterAction = () => {
    setIsOpenModalDeleteMeter(false);
  };

  const handClickOnCloseModalDeleteMeter = (response?: ModalResponse) => {
    if (response === ModalResponse.OK) {
      deleteMeters(checkedSdpIds)
        .then(() => {
          handleClickCancelOverviewMeterList();
          refetchGetOverview();
        })
        .catch(() => {
          setUiState(() => ({ hasError: true }));
        });
    }
  };

  const handleClickBurgerMenu = () => {
    setHasOverviewMetersList((prevState) => !prevState);
  };

  const handleClickCloseOverviewMeterList = () => {
    setHasOverviewMetersList(false);
  };

  const handleClickAddEan = () => {
    resetAddMeterWizardState();
    navigate(
      `${routes.METERS.path}${routes.METERS.children.METERS_ADD.path}${window.location.search}`
    );
  };

  return (
    <section className="container-fluid  overview h-100">
      <div className="row">
        {sdpAtom?.items?.length === 0 && !isFetchingGetOverview ? (
          <div className="col-12">
            <EmptyState
              message={t('overview.empty.state.message')}
              icon={<ImportantIcon />}
              dataTestId="overview-empty-state"
            >
              <Button
                onClick={handleClickAddEan}
                className="btn btn--primary"
                dataTestId="overview-empty-state-action-btn"
              >
                {t('overview.empty.state.action.message')}
              </Button>
            </EmptyState>
          </div>
        ) : (
          !isGetOverviewError && (
            <Fragment>
              <Container
                tag="aside"
                className={classNames('col-12 col-md-5 col-lg-4  overview__meters', {
                  'overview__meters--compact': hasOverviewMetersList,
                  'overview__meters--collapsed': !hasOverviewMetersList,
                })}
                fixedBottomElementClassName={classNames('col-12 col-md-5 col-lg-4', {
                  'custom-container__bottom-element--large':
                    isOverviewListEditMode && !isFetchingGetOverview && !isGetOverviewError,
                  'custom-container__bottom-element--compact': hasOverviewMetersList,
                  'custom-container__bottom-element--collapsed': !hasOverviewMetersList,
                })}
                fixedBottomElement={renderFixedBottomElement()}
              >
                <CloseIcon
                  role="button"
                  className="icon overview__meters__close-icon"
                  onClick={handleClickCloseOverviewMeterList}
                />
                {renderMetersGroup({
                  title: t('overview.my.meters'),
                  sdps: groupedSdps.ownSdps,
                  isEditable: isOverviewListEditMode === false,
                })}
                {renderMetersGroup({
                  title: t('overview.other.meters'),
                  sdps: groupedSdps.otherSdps,
                  isEditable: isOverviewListEditMode === false && groupedSdps.ownSdps.length === 0,
                })}
                {renderMetersGroup({
                  title: t('overview.waiting.meters'),
                  sdps: groupedSdps.waitingSdps,
                })}
                {renderMetersGroup({
                  title: t('overview.expired.meters'),
                  sdps: groupedSdps.expiredSdps,
                  isEditable:
                    isOverviewListEditMode === false &&
                    groupedSdps.otherSdps.length === 0 &&
                    groupedSdps.ownSdps.length === 0,
                })}
              </Container>
              <Container
                tag="article"
                className={classNames('col overview-meter-details', {
                  'overview-meter-details--compact': hasOverviewMetersList,
                  'overview-meter-details--collapsed': !hasOverviewMetersList,
                })}
              >
                <BurgerIcon
                  className="icon overview-meter-details__burger-icon"
                  role="button"
                  onClick={handleClickBurgerMenu}
                />
                <Outlet />
              </Container>
            </Fragment>
          )
        )}
      </div>
      {sdpAtom?.items &&
      sdpAtom.items.length > 0 &&
      !isFetchingGetOverview &&
      !isGetOverviewError ? (
        <div
          className={classNames('overview__collapse d-none d-md-block', {
            'overview__collapse--collapsed': !hasOverviewMetersList,
          })}
          onClick={handleClickBurgerMenu}
        >
          <ChevronLeftIcon className="icon icon--action" />
        </div>
      ) : !isFetchingGetOverview ? null : (
        <Skeleton
          count={1}
          height={40}
          width={40}
          borderRadius="100%"
          className="overview__collapse__skeleton  d-none d-md-block"
        />
      )}
      <ModalDeleteMeter
        isOpen={isOpenModalDeleteMeter}
        onClickAction={handleClickModalDeleteMeterAction}
        onClose={handClickOnCloseModalDeleteMeter}
      />
    </section>
  );
}
